import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BlogPost } from '../types/BlogPost';
import { getPublishedBlogPosts } from '../services/blogService';
import { trackBlogView } from '../services/analyticsService';

const BlogPage: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // SEO Meta Tags
      document.title = 'Car Recovery Blog - Tips, Guides & Industry News | Cheapest Car Recovery UK';
      
      const metaTags = [
        { name: 'description', content: 'Expert car recovery tips, breakdown guides, and automotive industry news. Stay informed with the UK\'s leading car recovery service blog.' },
        { name: 'keywords', content: 'car recovery blog, breakdown tips, vehicle recovery guides, automotive news UK, car maintenance tips, emergency breakdown advice' },
        { property: 'og:title', content: 'Car Recovery Blog - Expert Tips & Guides' },
        { property: 'og:description', content: 'Expert car recovery tips, breakdown guides, and automotive industry news from the UK\'s most trusted recovery service.' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://cheapestcarrecovery.uk/blog' }
      ];

      metaTags.forEach(tag => {
        const existingTag = document.querySelector(`meta[name="${tag.name}"], meta[property="${tag.property}"]`);
        if (existingTag) {
          existingTag.remove();
        }
        
        const metaTag = document.createElement('meta');
        if (tag.name) metaTag.setAttribute('name', tag.name);
        if (tag.property) metaTag.setAttribute('property', tag.property);
        metaTag.setAttribute('content', tag.content);
        document.head.appendChild(metaTag);
      });

      loadBlogPosts();
      trackBlogView(); // Track blog page view
    } catch (err) {
      console.error('Error in blog page useEffect:', err);
    }
  }, []);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const posts = await getPublishedBlogPosts();
      setBlogPosts(posts);
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const responsiveStyles = `
    @media (max-width: 768px) {
      .blog-grid {
        grid-template-columns: 1fr !important;
      }
      .hero-title {
        font-size: 32px !important;
      }
      .hero-gradient {
        padding: 40px 20px !important;
      }
      .floating-card {
        margin: 10px !important;
      }
      .blog-hero-bg {
        height: 300px !important;
      }
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes float {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-10px);
      }
    }
    
    .animate-fade-in {
      animation: fadeInUp 0.8s ease-out forwards;
    }
    
    .animate-float {
      animation: float 3s ease-in-out infinite;
    }
    
    .gradient-text {
              background: linear-gradient(135deg, #333 0%, #FFF222 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .glass-effect {
      backdrop-filter: blur(20px);
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
    
    .neon-glow {
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh' }}>
        {/* Modern Header with Glass Effect */}
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(51, 51, 51, 0.95)',
          backdropFilter: 'blur(20px)',
          color: 'white',
          padding: '15px 0',
          zIndex: 1000,
          borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Link to="/" style={{
                color: 'white', 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <img 
                  src="/my-logo.png" 
                  alt="Cheapest Car Recovery UK Logo" 
                  style={{ height: '35px', width: 'auto' }}
                />
                <span style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                }}>
                  CHEAPEST CAR RECOVERY UK
                </span>
              </Link>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                <Link to="/" style={{
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '16px',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFF222'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'white'}>
                  Home
                </Link>
                <Link to="/" className="neon-glow" style={{
                              backgroundColor: 'linear-gradient(135deg, #FFF222, #FFA500)',
            background: 'linear-gradient(135deg, #FFF222, #FFA500)',
                  color: '#333',
                  padding: '12px 24px',
                  borderRadius: '25px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  border: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 215, 0, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.3)';
                }}>
                  Get Quote
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Ultra Modern Hero Section */}
        <section className="blog-hero-bg" style={{
          height: '500px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          marginTop: '70px'
        }}>
          {/* Animated Background Elements */}
          <div style={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: '100px',
            height: '100px',
            background: 'rgba(255, 215, 0, 0.1)',
            borderRadius: '50%',
            animation: 'float 4s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            top: '60%',
            right: '15%',
            width: '80px',
            height: '80px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            animation: 'float 3s ease-in-out infinite reverse'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '20%',
            left: '20%',
            width: '60px',
            height: '60px',
            background: 'rgba(255, 215, 0, 0.2)',
            borderRadius: '50%',
            animation: 'float 5s ease-in-out infinite'
          }}></div>

          <div className="hero-gradient glass-effect" style={{
            textAlign: 'center',
            padding: '60px 40px',
            borderRadius: '20px',
            margin: '0 20px',
            maxWidth: '800px'
          }}>
            <h1 className="hero-title animate-fade-in gradient-text" style={{
              fontSize: '48px',
              fontWeight: '900',
              marginBottom: '25px',
              textShadow: '0 0 30px rgba(255, 215, 0, 0.3)'
            }}>
              🚗 Car Recovery Blog
            </h1>
            <p className="animate-fade-in" style={{
              fontSize: '20px',
              color: 'white',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              animationDelay: '0.2s'
            }}>
              Expert tips, guides, and industry news from the UK's most trusted car recovery service. 
              Stay ahead with professional insights! 🔧✨
            </p>
            <div className="animate-fade-in" style={{ 
              marginTop: '30px',
              animationDelay: '0.4s'
            }}>
              <span style={{
                background: 'rgba(255, 215, 0, 0.2)',
                color: 'white',
                padding: '8px 20px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}>
                📈 Updated Weekly • 🎯 Expert Content • 🏆 Trusted by 1000+ Customers
              </span>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section style={{
          padding: '60px 20px',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {error && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            {loading && (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#666'
              }}>
                <div style={{ fontSize: '18px' }}>Loading blog posts...</div>
              </div>
            )}

            {!loading && blogPosts.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#666'
              }}>
                <div style={{ fontSize: '18px' }}>No blog posts available yet.</div>
                <p>Check back soon for expert car recovery tips and guides!</p>
              </div>
            )}

            {!loading && blogPosts.length > 0 && (
              <div className="blog-grid animate-fade-in" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                gap: '35px'
              }}>
                {blogPosts.map((post, index) => (
                  <article key={post.id} className="floating-card" style={{
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: '20px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    border: '1px solid rgba(255, 215, 0, 0.1)',
                    animationDelay: `${index * 0.1}s`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-15px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.1)';
                  }}>
                    
                    {/* Gradient Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #FFF222, #FFA500, #FF6B6B, #4ECDC4)',
                      backgroundSize: '200% 100%',
                      animation: 'gradient-shift 3s ease infinite'
                    }}></div>

                    {post.imageUrl && (
                      <div style={{ 
                        height: '220px', 
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <img 
                          src={post.imageUrl}
                          alt={post.imageAlt || post.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.4s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        />
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '50px',
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.1))'
                        }}></div>
                      </div>
                    )}
                    
                    <div style={{ padding: '30px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                        {post.featured && (
                          <span style={{
                            background: 'linear-gradient(135deg, #FFF222, #FFA500)',
                            color: 'white',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)'
                          }}>
                            ⭐ FEATURED
                          </span>
                        )}
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#666',
                          background: '#f0f0f0',
                          padding: '4px 10px',
                          borderRadius: '12px'
                        }}>
                          ⏱️ {post.readingTime} min read
                        </span>
                      </div>

                      <h2 style={{
                        fontSize: '22px',
                        fontWeight: '800',
                        color: '#333',
                        marginBottom: '15px',
                        lineHeight: '1.4',
                        letterSpacing: '-0.5px'
                      }}>
                        <Link 
                          to={`/blog/${post.slug}`}
                          style={{ 
                            color: '#333', 
                            textDecoration: 'none',
                            background: 'linear-gradient(135deg, #333 0%, #FFF222 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            transition: 'all 0.3s ease'
                          }}
                                                     onMouseEnter={(e) => {
                             e.currentTarget.style.background = 'linear-gradient(135deg, #FFF222 0%, #FF6B6B 100%)';
                             (e.currentTarget.style as any).webkitBackgroundClip = 'text';
                             (e.currentTarget.style as any).webkitTextFillColor = 'transparent';
                           }}
                           onMouseLeave={(e) => {
                             e.currentTarget.style.background = 'linear-gradient(135deg, #333 0%, #FFF222 100%)';
                             (e.currentTarget.style as any).webkitBackgroundClip = 'text';
                             (e.currentTarget.style as any).webkitTextFillColor = 'transparent';
                           }}
                        >
                          {post.title}
                        </Link>
                      </h2>

                      <p style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#666',
                        marginBottom: '20px'
                      }}>
                        {post.description}
                      </p>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '13px',
                        color: '#888',
                        marginBottom: '20px',
                        padding: '10px',
                        background: '#f8f9fa',
                        borderRadius: '10px'
                      }}>
                        <span>👤 {post.author}</span>
                        <span>📅 {new Date(post.publishedAt).toLocaleDateString()}</span>
                      </div>

                      {post.tags.length > 0 && (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          marginBottom: '25px'
                        }}>
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} style={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '15px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
                            }}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <Link
                        to={`/blog/${post.slug}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'linear-gradient(135deg, #333 0%, #555 100%)',
                          color: 'white',
                          padding: '12px 24px',
                          borderRadius: '25px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #FFF222 0%, #FFA500 100%)';
                          e.currentTarget.style.color = '#333';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #333 0%, #555 100%)';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                        }}
                      >
                        Read Article 📖
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer style={{
          backgroundColor: '#333',
          color: 'white',
          padding: '40px 20px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '15px' }}>
              <img 
                src="/my-logo.png" 
                alt="Cheapest Car Recovery UK Logo" 
                style={{ height: '35px', width: 'auto' }}
              />
              <h3 style={{ fontSize: '20px', margin: 0 }}>
                Cheapest Car Recovery UK
              </h3>
            </div>
            <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '20px' }}>
              Professional car recovery services across the UK • Available 24/7
            </p>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div>📧 admin@cheapestcarrecovery.uk</div>
              <div>📞 +44 7432 387241</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPage; 